import CommentIcon from "@mui/icons-material/Comment";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import { Box, Button, Grid, Link, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { OverflowTooltip } from "../../../../ui/components/OverflowTooltip";
import { titleCase } from "../../../../../utils";
import {
  THistDocumentChange,
  THistDocumentDataChange,
  THistDocumentFieldChange,
  THistDocumentFieldType,
  THistDocumentFileChange,
  THistDocumentFileValue,
  THistDocumentValueChange,
  THistoryDocumentChangeBlock,
  THistoryDocumentFileChange,
} from "../../../models";
import { downloadHistoryFile } from "../../../network";
import {
  formatBooleanConfigValue,
  formatCountryDisplayValue,
  formatSourceOfActiveDisplayLines,
  formatUrlDisplayValue,
} from "../../../utils/configFieldDisplay";
import {
  EntityRepresentativesPerson,
  formatEntityRepresentativeResponsibleLabel,
} from "../../../utils/entityRepresentatives";
import {
  HistoryDocumentChangeGroupProps,
  HistoryEntryIcon,
  HistoryEntryMainText,
  UserInfoDisplay,
} from "./common";

const maxValueLength = 160;
const entityRepresentativesDocumentType = "EntityRepresentatives";

const rowOperationLabel = {
  create: "created",
  delete: "deleted",
  change: "updated",
};

const fileOperationLabel = {
  upload: "Uploaded",
  delete: "Deleted",
  replace: "Replaced",
};

type ValueFormatter = (value: unknown) => string;
type FileValueSide = "before" | "after";
type RenderFileValue = (
  change: THistoryDocumentFileChange,
  value: THistDocumentFileValue,
  side: FileValueSide,
  label: string,
) => JSX.Element;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const isEmptyHistoryValue = (value: unknown): boolean =>
  value === null || value === undefined || value === "";

const truncate = (value: string): string => {
  if (value.length <= maxValueLength) {
    return value;
  }
  return `${value.slice(0, maxValueLength - 1)}...`;
};

const formatEventDate = (date: Date): string => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");

  return `${dd}-${mm}-${yyyy}`;
};

const formatDateValue = (value: unknown): string => {
  if (isEmptyHistoryValue(value)) {
    return "";
  }

  if (typeof value === "string") {
    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (dateOnlyMatch) {
      return `${dateOnlyMatch[3]}-${dateOnlyMatch[2]}-${dateOnlyMatch[1]}`;
    }
  }

  const date = typeof value === "string" ? new Date(value) : (value as Date);
  if (isNaN(date.getTime())) {
    return formatRawValue(value);
  }

  return formatEventDate(date);
};

const formatRawValue = (value: unknown): string => {
  if (isEmptyHistoryValue(value)) {
    return "";
  }

  if (Array.isArray(value)) {
    const visibleValues = value.slice(0, 3).map(formatRawValue);
    const suffix = value.length > 3 ? ` (+${value.length - 3} more)` : "";
    return truncate(`${visibleValues.join(", ")}${suffix}`);
  }

  if (isRecord(value)) {
    return truncate(JSON.stringify(value));
  }

  if (typeof value === "string") {
    return truncate(value);
  }

  return truncate(String(value));
};

const fieldValueFormatters: Partial<Record<THistDocumentFieldType, ValueFormatter>> = {
  checkbox: formatBooleanConfigValue,
  countrySelect: formatCountryDisplayValue,
  date: formatDateValue,
  pep: formatBooleanConfigValue,
  sourceOfActive: (value) => formatSourceOfActiveDisplayLines(value).join(", "),
  url: formatUrlDisplayValue,
};

const formatFieldValue = (
  type: THistDocumentFieldType | undefined,
  value: unknown,
): string => {
  if (Array.isArray(value)) {
    return value
      .map((entry) => formatFieldValue(type, entry))
      .filter(Boolean)
      .join(", ");
  }

  return type
    ? fieldValueFormatters[type]?.(value) ?? formatRawValue(value)
    : formatRawValue(value);
};

const formatValueChange = (
  change: THistDocumentValueChange,
  fieldType?: THistDocumentFieldType,
): string => {
  const format = (value: unknown) =>
    fieldType ? formatFieldValue(fieldType, value) : formatRawValue(value);

  switch (change.operation) {
    case "add":
      return `Added ${format(change.after)}`;
    case "delete":
      return `Removed ${format(change.before)}`;
    case "change":
      if (isEmptyHistoryValue(change.before)) {
        return format(change.after);
      }
      if (isEmptyHistoryValue(change.after)) {
        return `Removed ${format(change.before)}`;
      }
      return `${format(change.before)} -> ${format(change.after)}`;
    default:
      return "";
  }
};

const fileLabel = (change: THistDocumentFileChange): string =>
  change.fieldLabel?.trim() ||
  change.fieldName?.trim() ||
  "User uploaded file";

const labelHasFileSuffix = (label: string): boolean => {
  const words = label
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase()
    .split(/\s+/);

  return ["file", "files"].includes(words[words.length - 1]);
};

const fileValueLabel = (change: THistDocumentFileChange): string => {
  const label = fileLabel(change);
  return labelHasFileSuffix(label) ? label : `${label} file`;
};

const fileKindLabel = (value: THistDocumentFileValue): string => {
  const kind = value.kind?.trim();
  return kind ? `Kind: ${kind}` : "";
};

const fileDownloadKey = (
  change: THistoryDocumentFileChange,
  value: THistDocumentFileValue,
  side: FileValueSide,
): string =>
  `${change.sourceHistoryId}:${change.documentDataId}:${side}:${value.downloadRef ?? ""}`;

const hiddenFileMetadataNames = new Set(["originalname", "mimetype", "size"]);

const isVisibleFileMetadataName = (name: string): boolean =>
  !hiddenFileMetadataNames.has(
    name.replace(/[_\s-]+/g, "").toLowerCase(),
  );

const formatDisplayName = (value: string): string =>
  value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .split(/\s+/)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(" ");

const documentLabel = (
  documentChange: Pick<
    THistDocumentChange | THistoryDocumentChangeBlock,
    "documentName" | "documentType"
  >,
): string => {
  const rawLabel = documentChange.documentName?.trim() || documentChange.documentType;
  return /\s/.test(rawLabel) ? rawLabel : formatDisplayName(rawLabel);
};

const documentFieldChanges = (
  documentChange: THistDocumentChange | THistoryDocumentChangeBlock,
): THistDocumentFieldChange[] =>
  (documentChange.document ?? []).flatMap((recordChange) => recordChange.fields ?? []);

const isCommentOnly = (documentChanges: THistoryDocumentChangeBlock[]): boolean =>
  documentChanges.every((change) => {
    const fields = documentFieldChanges(change);

    return (
      fields.length > 0 &&
      !change.data?.length &&
      !change.file?.length &&
      fields.every((field) => field.name === "comment")
    );
  });

const isDeleteOnly = (documentChanges: THistoryDocumentChangeBlock[]): boolean =>
  documentChanges.every((change) => {
    const documentDeletes =
      !change.document?.length ||
      change.document.every((documentChange) => documentChange.operation === "delete");
    const dataDeletes =
      !change.data?.length ||
      change.data.every((dataChange) => dataChange.operation === "delete");
    const fileDeletes =
      !change.file?.length ||
      change.file.every((fileChange) => fileChange.operation === "delete");

    return documentDeletes && dataDeletes && fileDeletes;
  });

const renderFieldChanges = (
  fields: THistDocumentFieldChange[] | undefined,
  keyPrefix: string,
) => (
  <Box sx={{ marginLeft: "12px" }}>
    {(fields ?? []).map((field, fieldIndex) => {
      const label = field.label || formatDisplayName(field.name);

      return (field.changes ?? []).map((change, changeIndex) => (
        <Box
          key={`${keyPrefix}-${field.name}-${fieldIndex}-${changeIndex}`}
          data-testid="history-field-change-row"
          sx={{
            alignItems: "baseline",
            columnGap: "16px",
            display: "grid",
            gridTemplateColumns: "minmax(180px, 260px) minmax(0, 1fr)",
            minWidth: 0,
          }}
        >
          <OverflowTooltip title={label}>
            <Typography
              data-testid="history-field-change-label"
              sx={{
                fontSize: "14px",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
          </OverflowTooltip>
          <Typography sx={{ fontSize: "14px", color: "grey" }}>
            {formatValueChange(change, field.type || undefined)}
          </Typography>
        </Box>
      ));
    })}
  </Box>
);

const hasFieldValueChanges = (
  fields: THistDocumentFieldChange[] | undefined,
): boolean => (fields ?? []).some((field) => (field.changes ?? []).length > 0);

const renderDocumentChanges = (documentChange: THistoryDocumentChangeBlock) =>
  documentChange.document
    .filter((recordChange) =>
      recordChange.operation !== "create" ||
      hasFieldValueChanges(recordChange.fields)
    )
    .map((recordChange, index) => (
      <Box key={`document-${index}`} sx={{ marginTop: "8px" }}>
        <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
          {recordChange.operation === "create"
            ? `Created ${documentLabel(documentChange)}`
            : `${titleCase(rowOperationLabel[recordChange.operation])} ${documentLabel(documentChange)}`}
        </Typography>
        {renderFieldChanges(recordChange.fields, `document-${index}`)}
      </Box>
    ));

const renderDataChange = (
  dataChange: THistDocumentDataChange,
  index: number,
) => {
  const key = `data-${dataChange.documentDataId}-${index}`;
  const fields = renderFieldChanges(dataChange.fields, key);

  if (dataChange.operation === "create") {
    return (
      <Box key={key} sx={{ marginTop: "8px" }}>
        {fields}
      </Box>
    );
  }

  return (
    <Box key={key} sx={{ marginTop: "8px" }}>
      <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
        {titleCase(rowOperationLabel[dataChange.operation])}{" "}
        {formatDisplayName(dataChange.dataId)}
      </Typography>
      {fields}
    </Box>
  );
};

const renderDataChanges = (documentChange: THistoryDocumentChangeBlock) =>
  documentChange.data?.map(renderDataChange);

type EntityRepresentativeHistoryPerson = Partial<EntityRepresentativesPerson> &
  Record<string, unknown>;

const toEntityRepresentativeHistoryPerson = (
  value: unknown,
): EntityRepresentativeHistoryPerson | null =>
  isRecord(value) ? (value as EntityRepresentativeHistoryPerson) : null;

const getPersonString = (
  person: EntityRepresentativeHistoryPerson,
  field: keyof EntityRepresentativesPerson,
): string => {
  const value = person[field];
  return typeof value === "string" ? value.trim() : "";
};

const formatEntityRepresentativeName = (
  person: EntityRepresentativeHistoryPerson,
): string => {
  const name = [
    getPersonString(person, "firstName"),
    getPersonString(person, "lastName"),
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return name || "Unnamed";
};

const formatEntityRepresentativeRoles = (
  person: EntityRepresentativeHistoryPerson,
): string => {
  const roles = [
    person.representative === true ? "Representative" : undefined,
    person.ubo === true ? "UBO" : undefined,
  ].filter(Boolean);

  return roles.join(", ");
};

const formatEntityRepresentativeContact = (
  person: EntityRepresentativeHistoryPerson,
): string =>
  [getPersonString(person, "email"), getPersonString(person, "phone")]
    .filter(Boolean)
    .join(", ");

const formatEntityRepresentativeDetails = (
  person: EntityRepresentativeHistoryPerson,
): string[] =>
  [
    formatEntityRepresentativeRoles(person),
    formatEntityRepresentativeResponsibleLabel(person.responsible),
    formatEntityRepresentativeContact(person),
  ].filter(Boolean);

const getEntityRepresentativeChangePerson = (
  change: THistDocumentValueChange,
): { action: string; person: EntityRepresentativeHistoryPerson | null } => {
  switch (change.operation) {
    case "add":
      return {
        action: "Added",
        person: toEntityRepresentativeHistoryPerson(change.after),
      };
    case "delete":
      return {
        action: "Removed",
        person: toEntityRepresentativeHistoryPerson(change.before),
      };
    case "change":
      if (isEmptyHistoryValue(change.before)) {
        return {
          action: "Added",
          person: toEntityRepresentativeHistoryPerson(change.after),
        };
      }
      if (isEmptyHistoryValue(change.after)) {
        return {
          action: "Removed",
          person: toEntityRepresentativeHistoryPerson(change.before),
        };
      }
      return {
        action: "Updated",
        person:
          toEntityRepresentativeHistoryPerson(change.after) ??
          toEntityRepresentativeHistoryPerson(change.before),
      };
    default:
      return { action: "", person: null };
  }
};

const renderEntityRepresentativePeopleChange = (
  change: THistDocumentValueChange,
  key: string,
) => {
  const { action, person } = getEntityRepresentativeChangePerson(change);
  if (!person) {
    return (
      <Box key={key}>
        <Typography sx={{ fontSize: "14px", color: "grey" }}>
          {formatValueChange(change)}
        </Typography>
      </Box>
    );
  }

  const details = formatEntityRepresentativeDetails(person);

  return (
    <Box key={key} sx={{ marginLeft: "12px", marginTop: "8px" }}>
      <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
        {action} {formatEntityRepresentativeName(person)}
      </Typography>
      {details.length > 0 && (
        <Box sx={{ marginLeft: "12px" }}>
          {details.map((detail) => (
            <Typography key={detail} sx={{ fontSize: "14px", color: "grey" }}>
              {detail}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

const renderEntityRepresentativesFieldChanges = (
  field: THistDocumentFieldChange,
  keyPrefix: string,
) =>
  (field.changes ?? []).map((change, changeIndex) =>
    renderEntityRepresentativePeopleChange(
      change,
      `${keyPrefix}-${field.name}-${changeIndex}`,
    ),
  );

const renderEntityRepresentativesDataChanges = (
  documentChange: THistoryDocumentChangeBlock,
) =>
  documentChange.data?.map((dataChange, index) => {
    const fields = dataChange.fields ?? [];
    const peopleFields = fields.filter((field) => field.name === "people");

    if (peopleFields.length === 0) {
      return renderDataChange(dataChange, index);
    }

    const otherFields = fields.filter((field) => field.name !== "people");
    const key = `entity-representatives-data-${dataChange.documentDataId}-${index}`;

    return (
      <Box key={key} sx={{ marginTop: "8px" }}>
        {peopleFields.flatMap((field, fieldIndex) =>
          renderEntityRepresentativesFieldChanges(
            field,
            `${key}-${fieldIndex}`,
          ),
        )}
        {otherFields.length > 0 &&
          renderFieldChanges(otherFields, `${key}-other`)}
      </Box>
    );
  });

const renderDocumentDataChanges = (
  documentChange: THistoryDocumentChangeBlock,
) =>
  documentChange.documentType === entityRepresentativesDocumentType
    ? renderEntityRepresentativesDataChanges(documentChange)
    : renderDataChanges(documentChange);

const renderMetadataUpdateDetails = (
  change: THistoryDocumentFileChange,
) =>
  (change.meta ?? [])
    .filter((metaChange) => isVisibleFileMetadataName(metaChange.name))
    .map((metaChange) => (
      <Typography
        key={metaChange.name}
        sx={{ fontSize: "14px", color: "grey" }}
      >
        {formatDisplayName(metaChange.name)}: {formatRawValue(metaChange.before)}
        {" -> "}
        {formatRawValue(metaChange.after)}
      </Typography>
    ));

const renderFileKindDetails = (value: THistDocumentFileValue) => {
  const kindLabel = fileKindLabel(value);
  if (!kindLabel) {
    return null;
  }

  return (
    <Box sx={{ marginLeft: "12px" }}>
      <Typography
        data-testid="history-file-kind"
        sx={{ fontSize: "14px", color: "grey" }}
      >
        {kindLabel}
      </Typography>
    </Box>
  );
};

const renderFileChangeDetails = (
  change: THistoryDocumentFileChange,
  renderFileValue: RenderFileValue,
) => {
  switch (change.operation) {
    case "upload":
      return (
        <>
          <Typography component="div" sx={{ fontSize: "14px", fontWeight: 700 }}>
            {fileOperationLabel.upload}{" "}
            {renderFileValue(change, change.after, "after", fileValueLabel(change))}
          </Typography>
          {renderFileKindDetails(change.after)}
        </>
      );
    case "delete":
      return (
        <>
          <Typography component="div" sx={{ fontSize: "14px", fontWeight: 700 }}>
            {fileOperationLabel.delete}{" "}
            {renderFileValue(change, change.before, "before", fileValueLabel(change))}
          </Typography>
          {renderFileKindDetails(change.before)}
        </>
      );
    case "replace":
      return (
        <>
          <Typography component="div" sx={{ fontSize: "14px", fontWeight: 700 }}>
            {fileOperationLabel.replace}{" "}
            {renderFileValue(change, change.before, "before", fileValueLabel(change))}
          </Typography>
          {renderFileKindDetails(change.before)}
          <Typography component="div" sx={{ fontSize: "14px", fontWeight: 700 }}>
            With {renderFileValue(change, change.after, "after", "file")}
          </Typography>
          {renderFileKindDetails(change.after)}
        </>
      );
    case "metadata_update":
      return (
        <>
          <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
            Updated {fileValueLabel(change)} properties:
          </Typography>
          <Box sx={{ marginLeft: "12px" }}>
            {renderMetadataUpdateDetails(change)}
          </Box>
        </>
      );
    default:
      return null;
  }
};

const renderFileChanges = (
  documentChange: THistoryDocumentChangeBlock,
  renderFileValue: RenderFileValue,
) =>
  documentChange.file?.map((fileChange, index) => (
    <Box key={`file-${fileChange.documentDataId}-${index}`} sx={{ marginTop: "8px" }}>
      {renderFileChangeDetails(fileChange, renderFileValue)}
    </Box>
  ));

export const HistoryItemDocumentChangeGroup = ({
  item,
  statusesConfig,
  verificationRequestId,
}: HistoryDocumentChangeGroupProps) => {
  const [expanded, setExpanded] = useState(false);
  const [downloadingFileKey, setDownloadingFileKey] = useState<string | null>(
    null,
  );
  const { enqueueSnackbar } = useSnackbar();
  const documentChanges = item.blocks;

  const handleToggleExpanded = (event: React.MouseEvent) => {
    event.preventDefault();
    setExpanded((value) => !value);
  };

  const handleDownloadFile = async (
    change: THistoryDocumentFileChange,
    value: THistDocumentFileValue,
    side: FileValueSide,
  ) => {
    if (!value.downloadRef) {
      return;
    }

    const key = fileDownloadKey(change, value, side);
    if (downloadingFileKey === key) {
      return;
    }

    setDownloadingFileKey(key);
    try {
      const { downloadUrl } = await downloadHistoryFile(
        verificationRequestId,
        change.sourceHistoryId,
        value.downloadRef,
      );

      if (!downloadUrl) {
        throw new Error("Empty history file download URL");
      }

      window.open(downloadUrl, "_blank", "noopener,noreferrer");
    } catch {
      enqueueSnackbar("Failed to download history file", { variant: "error" });
    } finally {
      setDownloadingFileKey((currentKey) =>
        currentKey === key ? null : currentKey,
      );
    }
  };

  const renderFileValue: RenderFileValue = (change, value, side, label) => {
    const key = fileDownloadKey(change, value, side);
    const isDownloading = downloadingFileKey === key;

    const labelNode = value.downloadRef ? (
      <Button
        type="button"
        size="small"
        disabled={isDownloading}
        onClick={() => handleDownloadFile(change, value, side)}
        sx={{
          fontSize: "14px",
          fontWeight: 700,
          lineHeight: "20px",
          minWidth: 0,
          padding: 0,
          textTransform: "none",
          verticalAlign: "baseline",
        }}
      >
        {label}
      </Button>
    ) : (
      <Typography component="span" sx={{ fontSize: "14px", fontWeight: 700 }}>
        {label}
      </Typography>
    );

    return (
      <>
        {labelNode}
      </>
    );
  };

  const getIcon = () => {
    if (isCommentOnly(documentChanges)) {
      return <CommentIcon sx={{ height: 20, color: "#607D8B" }} />;
    }
    if (isDeleteOnly(documentChanges)) {
      return <DeleteIcon sx={{ height: 20, color: "#D32F2F" }} />;
    }
    return <DescriptionIcon sx={{ height: 20, color: "#2196F3" }} />;
  };

  return (
    <>
      <HistoryEntryIcon>{getIcon()}</HistoryEntryIcon>

      <HistoryEntryMainText>Changed forms and documents</HistoryEntryMainText>

      <UserInfoDisplay item={item} statusesConfig={statusesConfig} />

      <Grid
        item
        xs={12}
        data-testid="history-document-change-details-row"
      >
        {!expanded && (
          <Link
            href="#"
            onClick={handleToggleExpanded}
            sx={{ cursor: "pointer", display: "inline-block", fontSize: "14px" }}
          >
            Show changes
          </Link>
        )}

        {expanded && (
          <>
            <Box sx={{ marginTop: "8px" }}>
              {documentChanges.map((documentChange, index) => (
                <Box
                  key={`${documentChange.documentId}-${index}`}
                  sx={{ marginTop: index === 0 ? 0 : "12px" }}
                >
                  <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
                    {documentLabel(documentChange)}
                  </Typography>
                  {renderDocumentChanges(documentChange)}
                  {renderDocumentDataChanges(documentChange)}
                  {renderFileChanges(documentChange, renderFileValue)}
                </Box>
              ))}
            </Box>
            <Link
              href="#"
              onClick={handleToggleExpanded}
              sx={{ cursor: "pointer", display: "inline-block", fontSize: "14px" }}
            >
              Hide changes
            </Link>
          </>
        )}
      </Grid>
    </>
  );
};
